import React, { useState, useEffect, useMemo } from 'react';
import { Text } from "@hangar/web-components/core/Text";
import { Button } from "@hangar/web-components/core/Button";
import { RadioGroup } from '@hangar/web-components/core/RadioGroup';
import { Radio } from '@hangar/web-components/core/Radio';
import { Alert } from '@ecargo/web-components';
import { HangarSelectField } from '../common/HangarSelectField';
import { useTranslation } from '../../hooks/i18n/useTranslation';
import * as S from './ChangeCodeForm.styles';
import {
  ChangeCodeFormProps,
  CASE_STATUS_ACTIONS,
  NOT_APPLICABLE_REASONS_LIST,
} from './ChangeCodeForm.types';
import { useChangeCodeForm } from './useChangeCodeForm';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { NotApplicableModal } from '../common/NotApplicableModal';
import { ActionConfirmationModal, HighlightText } from '../common/ActionConfirmationModal';

export type { ChangeReasonData, ChangeCodeFormProps, CaseStatusAction, ChangeCodeFormData, ChangeReasonPayload, SavedFormValues } from './ChangeCodeForm.types';
export { ACTION_TO_BACKEND_STATUS, CASE_STATUS_ACTIONS } from './ChangeCodeForm.types';

/** Uses eCargo `Alert` for inline form feedback — policy: `ECADCA-530-review.md` §2. */
export const ChangeCodeForm: React.FC<ChangeCodeFormProps> = ({
  changeReason,
  reasonCodes,
  onClose,
  onSave,
  readOnly = false,
  saveOnlySuccessMessage,
  onClearSuccessMessage,
  onHasChangesRef,
  savedValues,
}) => {
  const { t } = useTranslation();
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  
  const {
    reasonCodeCorrect,
    reasonCodeCorrectError,
    selectedReasonCodeId,
    selectedRemarkId,
    remarkError,
    newReasonCodeError,
    selectedInitialRemarkId,
    initialRemarkError,
    showValidationAlert,
    showNoChangesToSaveAlert,
    isRemarkRequired,
    availableRemarks,
    selectedAction,
    actionError,
    observations,
    hasChanges,
    showNotApplicableModal,
    showSendNotificationModal,
    showNonNotifiableRCModal,
    isSubmittingNotApplicable,
    isSubmittingSendNotification,
    isSubmittingNonNotifiableWarning,
    isSubmittingDirectSave,
    handleReasonCodeChange,
    handleNewReasonCodeChange,
    handleNewRemarkChange,
    handleInitialRemarkChange,
    handleActionChange,
    handleObservationsChange,
    handleSaveWithValidation,
    handleConfirmNotApplicable,
    handleCancelNotApplicable,
    handleConfirmSendNotification,
    handleCancelSendNotification,
    handleConfirmNonNotifiableRC,
    handleCancelNonNotifiableRC,
  } = useChangeCodeForm({ changeReason, reasonCodes, onSave, savedValues });

  const finalReasonCodeLabel = useMemo(() => {
    if (reasonCodeCorrect === 'yes') {
      return changeReason.reasonCode;
    }
    if (reasonCodeCorrect === 'no' && selectedReasonCodeId) {
      const selectedRC = reasonCodes.find(rc => rc.id === selectedReasonCodeId);
      if (selectedRC) {
        return `${selectedRC.reasonCode} - ${selectedRC.description}`;
      }
    }
    return '';
  }, [reasonCodeCorrect, selectedReasonCodeId, reasonCodes, changeReason.reasonCode]);

  useEffect(() => {
    onHasChangesRef?.(hasChanges);
  }, [onHasChangesRef, hasChanges]);

  const handleCloseClick = () => {
    if (isSubmittingDirectSave) return;
    if (hasChanges()) {
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedChangesModal(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedChangesModal(false);
  };

  return (
    <>
      <S.UnifiedCard>
        {showValidationAlert && (
          <S.ValidationAlertWrapper>
            <Alert
              description={t('flightChangeDetails.changeCodeForm.validation.completeRequiredFields')}
              variant="error"
            />
          </S.ValidationAlertWrapper>
        )}
        {showNoChangesToSaveAlert && (
          <S.ValidationAlertWrapper>
            <Alert
              description={t('flightChangeDetails.changeCodeForm.validation.noChangesToSave')}
              variant="warning"
            />
          </S.ValidationAlertWrapper>
        )}
        <S.TwoColumnSection>
          <S.ColumnContent>
            <S.ChangeReasonPanel $readOnly={readOnly}>
            <S.SectionTitleText>
              <S.ChangeCodeSectionTitle><Text variant="heading-md">{t('flightChangeDetails.changeCodeForm.changeReason.title')}</Text></S.ChangeCodeSectionTitle>
            </S.SectionTitleText>

            <S.FormGroup>
              <S.FormControlFixedWidth>
                <S.InitialReasonCodeLabel htmlFor="change-code-initial-reason-code" $formDisabled={readOnly}>
                  {t('flightChangeDetails.changeCodeForm.changeReason.initialReasonCode')}
                </S.InitialReasonCodeLabel>
                <S.InitialReasonCodeBox $formDisabled={readOnly}>
                  <S.InitialReasonCodeInput
                    id="change-code-initial-reason-code"
                    type="text"
                    value={changeReason.reasonCode}
                    readOnly={!readOnly}
                    disabled={readOnly}
                  />
                </S.InitialReasonCodeBox>
                <S.InitialReasonCodeHelperText $formDisabled={readOnly}>
                  {t('flightChangeDetails.changeCodeForm.changeReason.initialReasonCodeHelper')}
                </S.InitialReasonCodeHelperText>
              </S.FormControlFixedWidth>
            </S.FormGroup>

            {changeReason.isInitialRemarkRequired && (
              <S.FormGroup>
                <S.BorderlessFieldWrap>
                  <HangarSelectField
                    id="change-code-initial-remark"
                    label={`${t('flightChangeDetails.changeCodeForm.changeReason.initialRemark')} *`}
                    placeholder={t('flightChangeDetails.changeCodeForm.changeReason.select')}
                    value={selectedInitialRemarkId != null ? String(selectedInitialRemarkId) : ''}
                    options={changeReason.remarkOptions.map((remark) => ({
                      value: String(remark.id),
                      label: remark.description,
                    }))}
                    onChange={handleInitialRemarkChange}
                    disabled={readOnly}
                    readOnly={readOnly}
                    error={initialRemarkError}
                    errorText={
                      initialRemarkError
                        ? t('flightChangeDetails.changeCodeForm.changeReason.initialRemarkRequiredError')
                        : ''
                    }
                  />
                </S.BorderlessFieldWrap>
              </S.FormGroup>
            )}
            <S.RadioGroupBlock $hasError={reasonCodeCorrectError}>
              <S.ChangeReasonRadioGroupWrap $readOnly={readOnly}>
                <RadioGroup
                  id="change-code-reason-correct-rg"
                  value={reasonCodeCorrect ?? undefined}
                  onChange={(e) =>
                    handleReasonCodeChange(e.target.value as 'yes' | 'no')
                  }
                  disabled={readOnly}
                  error={reasonCodeCorrectError}
                  errorDescription={
                    reasonCodeCorrectError
                      ? t('flightChangeDetails.changeCodeForm.changeReason.reasonCodeCorrectRequiredError')
                      : undefined
                  }
                  groupLabel={`${t('flightChangeDetails.changeCodeForm.changeReason.isReasonCodeCorrect')} *`}
                >
                  <Radio
                    id="change-code-reason-correct-yes"
                    label={t('flightChangeDetails.changeCodeForm.changeReason.keepSame')}
                    value="yes"
                  />
                  <Radio
                    id="change-code-reason-correct-no"
                    label={t('flightChangeDetails.changeCodeForm.changeReason.registerNew')}
                    value="no"
                  />
                </RadioGroup>
              </S.ChangeReasonRadioGroupWrap>
            </S.RadioGroupBlock>

            {reasonCodeCorrect === 'no' && (
              <>
                <S.ReasonCodeContainer>
                  <S.FormGroup>
                    <S.ChangeReasonNewFieldsSelectWrap>
                      <HangarSelectField
                        id="change-code-new-reason-code"
                        label={`${t('flightChangeDetails.changeCodeForm.changeReason.newReasonCode')} *`}
                        placeholder={t('flightChangeDetails.changeCodeForm.changeReason.select')}
                        value={selectedReasonCodeId != null ? String(selectedReasonCodeId) : ''}
                        options={reasonCodes.map((rc) => ({
                          value: String(rc.id),
                          label: `${rc.reasonCode} - ${rc.description}`,
                        }))}
                        onChange={handleNewReasonCodeChange}
                        disabled={readOnly}
                        readOnly={readOnly}
                        error={newReasonCodeError}
                        errorText={
                          newReasonCodeError
                            ? t('flightChangeDetails.changeCodeForm.changeReason.newReasonCodeRequiredError')
                            : ''
                        }
                      />
                    </S.ChangeReasonNewFieldsSelectWrap>
                    <S.FormHelperText $readOnly={readOnly}>
                      {t('flightChangeDetails.changeCodeForm.changeReason.newReasonCodeHelper')}
                    </S.FormHelperText>
                  </S.FormGroup>

                  {isRemarkRequired && (
                    <S.FormGroup>
                      <S.ChangeReasonNewFieldsSelectWrap>
                        <HangarSelectField
                          id="change-code-new-remark"
                          label={`${t('flightChangeDetails.changeCodeForm.changeReason.newRemark')} *`}
                          placeholder={t('flightChangeDetails.changeCodeForm.changeReason.select')}
                          value={selectedRemarkId != null ? String(selectedRemarkId) : ''}
                          options={availableRemarks.map((remark) => ({
                            value: String(remark.id),
                            label: remark.description,
                          }))}
                          onChange={handleNewRemarkChange}
                          disabled={readOnly}
                          readOnly={readOnly}
                          error={remarkError}
                          errorText={
                            remarkError
                              ? t('flightChangeDetails.changeCodeForm.changeReason.remarkRequiredError')
                              : ''
                          }
                        />
                      </S.ChangeReasonNewFieldsSelectWrap>
                    </S.FormGroup>
                  )}
                </S.ReasonCodeContainer>
              </>
            )}
            </S.ChangeReasonPanel>
          </S.ColumnContent>

          <S.ColumnDivider aria-hidden />

          <S.ColumnContent>
            <S.SectionTitleText>
              <S.ChangeCodeSectionTitle><Text variant="heading-md">{t('flightChangeDetails.changeCodeForm.observations.title')}</Text></S.ChangeCodeSectionTitle>
            </S.SectionTitleText>
            <S.FormGroup>
              <S.BorderlessFieldWrap>
                <HangarSelectField
                  id="change-code-case-action"
                  label={`${t('flightChangeDetails.changeCodeForm.observations.caseAction')} *`}
                  placeholder={t('flightChangeDetails.changeCodeForm.changeReason.select')}
                  value={selectedAction ?? ''}
                  options={[
                    {
                      value: CASE_STATUS_ACTIONS.SEND_NOTIFICATION,
                      label: t('flightChangeDetails.changeCodeForm.observations.caseStatusActions.sendNotification'),
                    },
                    {
                      value: CASE_STATUS_ACTIONS.SAVE_ONLY,
                      label: t('flightChangeDetails.changeCodeForm.observations.caseStatusActions.saveOnly'),
                    },
                    {
                      value: CASE_STATUS_ACTIONS.ON_HOLD,
                      label: t('flightChangeDetails.changeCodeForm.observations.caseStatusActions.onHold'),
                    },
                    {
                      value: CASE_STATUS_ACTIONS.NOT_APPLICABLE,
                      label: t('flightChangeDetails.changeCodeForm.observations.caseStatusActions.notApplicable'),
                    },
                  ]}
                  onChange={handleActionChange}
                  disabled={readOnly}
                  readOnly={readOnly}
                  error={actionError}
                  errorText={
                    actionError
                      ? t('flightChangeDetails.changeCodeForm.observations.actionRequired')
                      : ''
                  }
                />
              </S.BorderlessFieldWrap>
            </S.FormGroup>

            <S.FormGroup>
              <S.ObservationsOptionalLabel htmlFor="change-code-observations" $readOnly={readOnly}>
                {t('flightChangeDetails.changeCodeForm.observations.observationsOptional')}
              </S.ObservationsOptionalLabel>
              <S.ObservationsInput
                id="change-code-observations"
                type="text"
                placeholder=""
                disabled={readOnly}
                value={observations}
                onChange={handleObservationsChange}
              />
            </S.FormGroup>
          </S.ColumnContent>
        </S.TwoColumnSection>
      </S.UnifiedCard>
      <S.Footer>
        <Button
          variant="secondary"
          label={t('flightChangeDetails.changeCodeForm.actions.close')}
          onClick={handleCloseClick}
          disabled={isSubmittingDirectSave}
        />
        {!readOnly && (
          <Button
            variant="primary"
            loading={isSubmittingDirectSave}
            aria-busy={isSubmittingDirectSave}
            aria-disabled={isSubmittingDirectSave}
            label={
              isSubmittingDirectSave
                ? t('actionConfirmation.processing')
                : t('flightChangeDetails.changeCodeForm.actions.save')
            }
            onClick={handleSaveWithValidation}
          />
        )}
      </S.Footer>

      <ConfirmationModal
        isOpen={showUnsavedChangesModal}
        title={t('flightChangeDetails.closeConfirmation.title')}
        message={t('flightChangeDetails.closeConfirmation.message')}
        confirmLabel={t('flightChangeDetails.closeConfirmation.confirm')}
        cancelLabel={t('flightChangeDetails.closeConfirmation.cancel')}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />

      <NotApplicableModal
        isOpen={showNotApplicableModal}
        reasons={NOT_APPLICABLE_REASONS_LIST}
        onConfirm={handleConfirmNotApplicable}
        onCancel={handleCancelNotApplicable}
        isSubmitting={isSubmittingNotApplicable}
      />

      <ActionConfirmationModal
        isOpen={showNonNotifiableRCModal}
        variant="warning"
        title={t('flightChangeDetails.nonNotifiableRCWarning.title')}
        message={t('flightChangeDetails.nonNotifiableRCWarning.message')}
        confirmLabel={t('flightChangeDetails.nonNotifiableRCWarning.confirm')}
        cancelLabel={t('flightChangeDetails.nonNotifiableRCWarning.cancel')}
        onConfirm={handleConfirmNonNotifiableRC}
        onCancel={handleCancelNonNotifiableRC}
        isSubmitting={isSubmittingNonNotifiableWarning}
      />

      <ActionConfirmationModal
        isOpen={showSendNotificationModal}
        variant="confirmation"
        title={t('flightChangeDetails.sendNotificationConfirmation.title')}
        message={
          <>
            <Text variant="body-md">
              {t('flightChangeDetails.sendNotificationConfirmation.message')}{' '}
              <HighlightText>[{finalReasonCodeLabel}]</HighlightText>.
            </Text>
          </>
        }
        confirmLabel={t('flightChangeDetails.sendNotificationConfirmation.confirm')}
        cancelLabel={t('flightChangeDetails.sendNotificationConfirmation.cancel')}
        onConfirm={handleConfirmSendNotification}
        onCancel={handleCancelSendNotification}
        isSubmitting={isSubmittingSendNotification}
      />
    </>
  );
};
